import {
  type IControlSettingUI,
  type IControlSettingData,
  EDependentControlAction,
} from '~/presentation/reports/common/type';
import type { SelectOptionType, SelectValueType } from '~/presentation/reports/ui/type';

import {
  createVariables,
  updateSettingsByConditions,
} from '~/presentation/reports/common/Control/utils';

type ControlValueMap = Record<
  string,
  {
    value?: SelectValueType;
    options?: SelectOptionType[];
    defaultTab?: string;
  }
>;

export const updateControlSettingData = (data: {
  settings: IControlSettingUI[];
  values?: IControlSettingData;
  allControlIds: Set<string>;
}): IControlSettingUI[] => {
  const { settings, values, allControlIds } = data;

  const controlValueMap: ControlValueMap = {};
  settings.forEach((setting) => collectControlValue(setting, controlValueMap, values));

  const updatedSettings = settings
    .map((setting) =>
      applyControlValueMap({ control: setting, map: controlValueMap, values, allControlIds }),
    )
    .filter((item) => !!item);

  const variables = createVariables(values);
  return updateSettingsByConditions(updatedSettings, variables);
};

const storeControlValue = (
  control: {
    id: string;
    defaultValue?: SelectValueType;
    options?: SelectOptionType[];
    defaultTab?: string;
  },
  map: ControlValueMap,
) => {
  if (!control.defaultValue && !control.options && !control.defaultTab) return;

  map[control.id] = {
    value: control.defaultValue,
    options: control.options,
    defaultTab: control.defaultTab,
  };
};

const collectControlValue = (
  control: IControlSettingUI,
  map: ControlValueMap,
  externalValues?: IControlSettingData,
): IControlSettingUI => {
  const mergedValue = externalValues?.[control.id]?.value ?? control.defaultValue;
  storeControlValue({ ...control, defaultValue: mergedValue }, map);

  const updatedConfigs = control.configs?.map((config) => {
    const mergedDefaultTab = externalValues?.[config.id]?.defaultTab ?? config.defaultTab;
    const mergedValue = externalValues?.[config.id]?.value ?? config.defaultValue;
    storeControlValue({ ...config, defaultValue: mergedValue, defaultTab: mergedDefaultTab }, map);
    return config;
  });

  const updatedChildren = control.controls?.map((child) =>
    collectControlValue(child, map, externalValues),
  );

  return {
    ...control,
    configs: updatedConfigs,
    controls: updatedChildren,
  };
};

const applyControlValueMap = (data: {
  control: IControlSettingUI;
  map: ControlValueMap;
  values?: IControlSettingData;
  allControlIds: Set<string>;
}): IControlSettingUI | null => {
  const { control, map, values, allControlIds } = data;
  const updatedConfigs = control.configs?.map((config) => ({
    ...config,
    defaultValue: map[config.id]?.value,
    defaultTab: map[config.id]?.defaultTab,
  }));

  const updatedChildren = control.controls
    ?.map((child) => applyControlValueMap({ control: child, map, values, allControlIds }))
    .filter((item) => !!item);

  const updatedControl = {
    ...control,
    configs: updatedConfigs,
    controls: updatedChildren,
  };

  updatedControl.defaultValue = map[control.id]?.value;

  const resolvedConnectorOptions = resolveConnectorOptions(updatedControl, map);

  const resolvedDependentControls = resolveDependentControls({
    control: resolvedConnectorOptions,
    values,
    allControlIds,
  });

  return resolvedDependentControls;
};

const resolveConnectorOptions = (
  control: IControlSettingUI,
  map: ControlValueMap,
): IControlSettingUI => {
  if (!control.connector?.options) return control;

  const sourceControlId = control.connector.options;
  const sourceData = map[sourceControlId];
  const selectedValues = sourceData?.value as string[];

  if (!sourceData || !selectedValues) return control;

  control.options = selectedValues
    .map((val) => sourceData.options?.find((opt) => opt.value === val))
    .filter((opt): opt is SelectOptionType => !!opt);

  const hasValueInOptions = control.options?.some((opt) => control.defaultValue === opt.value);

  //case user remove option has default value
  const defaultValue = hasValueInOptions ? control.defaultValue : control.options?.[0].value;
  return {
    ...control,
    defaultValue: defaultValue as string,
  };
};

const resolveDependentControls = (data: {
  control: IControlSettingUI;
  values?: IControlSettingData;
  allControlIds: Set<string>;
}): IControlSettingUI | null => {
  const { control, values, allControlIds } = data;
  const { dependentControls } = control;

  if (!dependentControls) return control;

  const isHideControl = dependentControls.some(({ action, id }) => {
    const value = allControlIds.has(id) ? values?.[id]?.value : undefined;
    return action === EDependentControlAction.HIDE && !value;
  });

  if (isHideControl) return null;

  const newDependentControls = dependentControls.map((dependentControl) => {
    const value = values?.[dependentControl.id]?.value;
    return {
      ...dependentControl,
      value: allControlIds.has(dependentControl.id) ? value : undefined,
    };
  });

  return {
    ...control,
    dependentControls: newDependentControls,
  };
};

export const getControlIdsById = (controls: IControlSettingUI[], id: string): string[] => {
  const targetControl = controls.find((item) => item.id === id);
  if (!targetControl) return [];

  const result: Set<string> = new Set();

  const traverse = (control: IControlSettingUI, firstLevel?: boolean) => {
    if (!firstLevel) {
      result.add(control.id);
    }

    control.configs?.forEach((cfg) => {
      result.add(cfg.id);
      cfg.tabs?.forEach((tab) => {
        result.add(tab.id);
      });
    });

    control.controls?.forEach((child) => {
      traverse(child, false);
    });
  };

  traverse(targetControl, true);

  return Array.from(result);
};

export const getControlValueById = (
  controlIds: string[],
  data?: IControlSettingData,
): Record<string, any> => {
  const result: Record<string, any> = {};
  controlIds?.forEach((id) => {
    if (!data?.[id]) return;

    const value = data[id].value;
    result[id] = value;
  });
  return result;
};

export const getAllControlIds = (controls: IControlSettingUI[]): Set<string> => {
  const result = new Set<string>();

  const traverse = (controls: IControlSettingUI[]) => {
    for (const control of controls) {
      result.add(control.id);

      control.configs?.forEach((c) => {
        result.add(c.id);

        c.tabs?.forEach((tab) => result.add(tab.id));
      });
      if (control.controls?.length) traverse(control.controls);
    }
  };

  traverse(controls);
  return result;
};
